import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { useGetUserProfileQuery } from "../../../../../api/userAuthApi";
import { useProfileAddressUpdate } from "../../../../../hooks/useProfileAddressUpdate";
import { areAddressesEqual } from "../../../../../../../common/helpers/addressHelpers";

import {
  createBillingSchema,
  BillingFormData,
} from "../../../../../../ecommerce/checkout/helpers/checkoutValidation";

import PersonalInfoFields from "../../../../../../ecommerce/checkout/components/common/CheckoutDetailsForm/PersonalInfoFields";
import AddressFields from "../../../../../../ecommerce/checkout/components/common/CheckoutDetailsForm/AddressFields";
import ContactInfoFields from "../../../../../../ecommerce/checkout/components/common/CheckoutDetailsForm/ContactInfoFields";
import { UpdateProfilePayload } from "../../../../../types/userAuthApiTypes";

interface ProfileBillingFormProps {
  useBillingForShipping: boolean;
  setUseBillingForShipping: (value: boolean) => void;
}

const ProfileBillingForm: React.FC<ProfileBillingFormProps> = ({
  useBillingForShipping,
  setUseBillingForShipping,
}) => {
  const { t } = useTranslation();

  const { data: profile, isLoading: isProfileLoading } =
    useGetUserProfileQuery();

  const { handleUpdate, isUpdating } = useProfileAddressUpdate("billing");

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<BillingFormData>({
    resolver: zodResolver(createBillingSchema(t)),
    defaultValues: {
      customerType: "individual",
      country: "", // Use empty to force select or placeholder
    },
  });

  useEffect(() => {
    if (profile?.billingAddress) {
      // Logic: If there is only 1 allowed country, pre-select it if the user profile has no country saved.
      const allowedCountries =
        window.checkoutFlowVariables?.allowedCountries || {};
      const countryCodes = Object.keys(allowedCountries);
      const singleCountryCode =
        countryCodes.length === 1 ? countryCodes[0] : "";
      const countryToSet =
        profile.billingAddress.country || singleCountryCode || "";

      reset({
        ...profile.billingAddress,
        customerType: profile.billingAddress.customerType || "individual",
        country: countryToSet,
        tckn: profile.billingAddress.tckn || "",
        vkn: profile.billingAddress.vkn || "",
      });
    }
  }, [profile, reset]);

  // Determine if the server currently has synced addresses.
  // We use this to enable the button if the user checks the box to force a sync.
  const isSyncedOnServer = profile
    ? areAddressesEqual(profile.billingAddress, profile.shippingAddress)
    : true;

  // Enable save if:
  // 1. Form fields are dirty
  // 2. OR User checked "Use Billing for Shipping" AND they were previously different on server
  const canSubmit = isDirty || (useBillingForShipping && !isSyncedOnServer);

  const onSubmit = (data: BillingFormData) => {
    // 1. Clean Contextual Data
    const isTurkey = data.country === "TR";

    // Only send TCKN/VKN if country is Turkey. Otherwise send empty string.
    const cleanTckn =
      isTurkey && data.customerType === "individual" ? data.tckn : "";
    const cleanVkn =
      isTurkey && data.customerType === "corporate" ? data.vkn : "";

    // Only send Company if Corporate (Global rule)
    const cleanCompany = data.customerType === "corporate" ? data.company : "";

    const billingPayload = {
      first_name: data.firstName,
      last_name: data.lastName,
      company: cleanCompany,
      address_1: data.address1,
      address_2: data.address2,
      city: data.city,
      state: data.state,
      postcode: data.postcode,
      country: data.country,
      email: data.email,
      phone: data.phone,
    };

    const additionalPayload = {
      customer_type: data.customerType,
      tckn: cleanTckn,
      vkn: cleanVkn,
    };

    const finalPayload: UpdateProfilePayload = {
      billing_address: billingPayload,
      additional_billing_info: additionalPayload,
    };

    if (useBillingForShipping) {
      // Create a copy of the billing payload for shipping, but omit the email.
      const { email, ...shippingData } = billingPayload;
      finalPayload.shipping_address = shippingData;
    }

    handleUpdate(finalPayload);
  };

  if (isProfileLoading) {
    return (
      <div className="profile-address-form">
        <Skeleton count={8} height={40} style={{ marginBottom: "1rem" }} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="profile-address-form">
      <h3 className="profile-address-form__title">
        {t("user.addresses.billingTitle", "Billing Address")}
      </h3>

      <div className="profile-address-form__content">
        <PersonalInfoFields
          register={register}
          errors={errors}
          watch={watch}
          setValue={setValue}
          variant="billing"
        />

        <AddressFields
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <ContactInfoFields
          register={register}
          errors={errors}
          showEmail={true}
        />
      </div>

      <div className="profile-address-form__footer">
        <div className="form-field checkbox-field ship-to-different-address-toggle">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={useBillingForShipping}
              onChange={(e) => setUseBillingForShipping(e.target.checked)}
            />
            <span className="checkbox-text">
              {t(
                "user.addresses.useBillingForShipping",
                "Use this address for shipping"
              )}
            </span>
          </label>
        </div>

        <button
          type="submit"
          className="button button--primary profile-address-form__submit-btn"
          disabled={isUpdating || !canSubmit}
        >
          {isUpdating
            ? t("user.accountDetails.saving", "Saving...")
            : t("user.accountDetails.saveChanges", "Save Changes")}
        </button>
      </div>
    </form>
  );
};

export default ProfileBillingForm;
